import { trace } from '@opentelemetry/api';
import { getCookie } from '@/utils/cookie';

// Jaeger远程采样配置
export interface JaegerRemoteSamplingConfig {
  enabled: boolean;
  endpoint: string;
  initialSamplingRate?: number;  // 初始采样率，远程获取后会被覆盖
}

// OpenTelemetry配置
export interface TelemetryConfig {
  serviceName: string;
  environment: string;
  enableConsole?: boolean;
  otlpEndpoint?: string;
  otlpHeaders?: Record<string, string>;
  samplingRate?: number;
  jaegerRemoteSampling?: JaegerRemoteSamplingConfig;
}

export class TelemetryManager {
  private isInitialized = false;
  private jaegerRemoteSampler?: any;
  private currentConfig?: TelemetryConfig;

  initialize(config: TelemetryConfig): void {
    if (this.isInitialized) {
      console.warn('OpenTelemetry already initialized');
      return;
    }

    this.currentConfig = config;

    this.initializeAsync(config).then(() => {
      this.isInitialized = true;
    }).catch(error => {
      console.error('❌ OpenTelemetry initialization failed:', error);
    });
  }

  /**
   * 停止远程采样器
   */
  async shutdown(): Promise<void> {
    if (this.jaegerRemoteSampler && typeof this.jaegerRemoteSampler.close === 'function') {
      try {
        await this.jaegerRemoteSampler.close();
      } catch (error) {
        console.warn('Failed to close Jaeger remote sampler:', error);
      }
    }
  }

  private async initializeAsync(config: TelemetryConfig): Promise<void> {
    try {
      // 动态导入 OpenTelemetry 模块以避免静态导入错误
      const { WebTracerProvider, BatchSpanProcessor, ConsoleSpanExporter } = await import('@opentelemetry/sdk-trace-web');
      const { Resource } = await import('@opentelemetry/resources');
      const { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } = await import('@opentelemetry/semantic-conventions');
      const { TraceIdRatioBasedSampler } = await import('@opentelemetry/sdk-trace-base');
      const { OTLPTraceExporter } = await import('@opentelemetry/exporter-trace-otlp-http');
      const { CompositePropagator, W3CTraceContextPropagator, W3CBaggagePropagator } = await import('@opentelemetry/core');
      const { propagation } = await import('@opentelemetry/api');

      // 设置全局传播器
      propagation.setGlobalPropagator(
        new CompositePropagator({
          propagators: [
            new W3CTraceContextPropagator(),
            new W3CBaggagePropagator()
          ]
        })
      );

      // 🔥 检查运行环境（使用 ViteSSG 设置的标志）
      const isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';
      const isClient = typeof window !== 'undefined' && (window as any).__IS_CLIENT__;
      const isSSG = typeof window !== 'undefined' && (window as any).__IS_SSG__;
      const isNode = typeof process !== 'undefined' && process.versions && process.versions.node;

      // 只在真正的客户端环境使用远程采样
      const isClientSide = isBrowser && (isClient === true) && !isSSG && !isNode;

      // 创建资源
      const resource = new Resource({
        [ATTR_SERVICE_NAME]: config.serviceName,
        [ATTR_SERVICE_VERSION]: '1.0.0',
        'deployment.environment': config.environment,
        // 🔥 添加自定义全局属性
        'user.timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        'd': getCookie('d'),
        'uid': sessionStorage.getItem('UID')
      });

      // 创建采样器 - 支持Jaeger远程采样（仅在客户端环境）
      let sampler: any;

      // 只在客户端环境且配置启用时使用Jaeger远程采样
      if (config.jaegerRemoteSampling?.enabled && isClientSide) {
        try {
          // 动态导入Jaeger Remote Sampler
          const { JaegerRemoteSampler } = await import('@opentelemetry/sampler-jaeger-remote');

          // 创建初始采样器
          const initialSamplingRate = config.jaegerRemoteSampling.initialSamplingRate ?? config.samplingRate ?? 1.0;
          const initialSampler = new TraceIdRatioBasedSampler(initialSamplingRate);

          // 创建Jaeger远程采样器配置
          const jaegerConfig = {
            endpoint: config.jaegerRemoteSampling.endpoint,
            serviceName: config.serviceName,
            initialSampler: initialSampler,
            poolingInterval: 60 * 1000  // 60秒轮询一次
          };

          // 创建远程采样器
          this.jaegerRemoteSampler = new JaegerRemoteSampler(jaegerConfig);
          sampler = this.jaegerRemoteSampler;

        } catch (jaegerError) {
          console.error('❌ Failed to initialize Jaeger Remote Sampler, falling back to static sampler:', jaegerError);
          sampler = new TraceIdRatioBasedSampler(config.samplingRate || 1.0);
        }
      } else {
        // 使用静态采样器（SSR/SSG环境或未启用远程采样）
        sampler = new TraceIdRatioBasedSampler(config.samplingRate || 1.0);
      }

      // 🔥 创建自定义SpanProcessor来修改HTTP请求的span名称并添加动态属性
      class HttpSpanNameProcessor {
        forceFlush() {
          return Promise.resolve();
        }

        shutdown() {
          return Promise.resolve();
        }

        onStart(span: any, parentContext: any) {
          this.updateSpanName(span);
          this.addCustomAttributes(span);
        }

        onEnd(span: any) {
          // span结束时添加结束属性
          this.addEndAttributes(span);
        }

        private addCustomAttributes(span: any) {
          try {
            // 🔥 添加动态自定义属性
            span.setAttributes({
              'page.url': typeof window !== 'undefined' ? window.location.href : 'unknown',
              'page.referrer': typeof document !== 'undefined' ? document.referrer : 'unknown',
              'user.timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
              'd': getCookie('d'),
              'uid': sessionStorage.getItem('UID')
            });

            // 🔥 根据span类型添加特定属性
            const spanKind = span.kind;
            if (spanKind === 3) { // CLIENT span (HTTP requests)
              span.setAttributes({
                'http.request_id': crypto.randomUUID(),
                'http.start_time': performance.now()
              });
            }
          } catch (error) {
            // 添加属性失败时忽略，避免影响主流程
          }
        }

        private addEndAttributes(span: any) {
          try {
            // 🔥 span 结束时添加的属性
            span.setAttributes({
              'span.end_timestamp': Date.now(),
              'span.duration_ms': span.duration ? span.duration[0] * 1000 + span.duration[1] / 1000000 : 0
            });

            // 如果是HTTP请求，添加更多信息
            const attributes = span.attributes || {};
            if (attributes['http.url']) {
              const httpStatus = attributes['http.status_code'];
              span.setAttributes({
                'http.success': !httpStatus || httpStatus < 400,
                'http.error_type': httpStatus >= 400 ? (httpStatus >= 500 ? 'server_error' : 'client_error') : 'none'
              });
            }
          } catch (error) {
            // 添加属性失败时忽略
          }
        }

        private updateSpanName(span: any) {
          try {
            const attributes = span.attributes || {};
            if (attributes['http.url'] && (span.kind === 3 || span.name.includes('HTTP') || span.name === 'GET' || span.name === 'POST' || span.name === 'PUT' || span.name === 'DELETE')) {
              const url = attributes['http.url'];
              const method = attributes['http.method'] || 'GET';
              try {
                const parsedUrl = new URL(url);
                const newName = `${method} ${parsedUrl.pathname}`;

                if (typeof span.updateName === 'function') {
                  span.updateName(newName);
                } else if (typeof span.setName === 'function') {
                  span.setName(newName);
                } else if (span._name !== undefined) {
                  span._name = newName;
                } else if (span.name !== undefined) {
                  span.name = newName;
                }
              } catch (urlError) {
                // URL解析失败时忽略
              }
            }
          } catch (error) {
            // 更新span名称失败时忽略
          }
        }
      }

      // 准备span processors
      const spanProcessors: any[] = [];

      // 添加控制台导出器（开发环境）
      if (config.enableConsole) {
        spanProcessors.push(new BatchSpanProcessor(new ConsoleSpanExporter()));
      }

      // 添加OTLP导出器
      if (config.otlpEndpoint) {
        const otlpExporter = new OTLPTraceExporter({
          url: config.otlpEndpoint,
          headers: config.otlpHeaders || {}
        });
        spanProcessors.push(new BatchSpanProcessor(otlpExporter));
      }

      // 创建提供者
      const provider = new WebTracerProvider({
        resource,
        sampler,
        spanProcessors
      });

      // 添加自定义SpanProcessor
      provider.addSpanProcessor(new HttpSpanNameProcessor());

      // 设置全局传播器
      try {
        propagation.setGlobalPropagator(
          new CompositePropagator({
            propagators: [
              new W3CTraceContextPropagator(),
              new W3CBaggagePropagator()
            ]
          })
        );
      } catch (propagationError) {
        console.warn('Failed to setup propagation:', propagationError);
      }

      // 注册提供者
      provider.register();

      // 只启用 HTTP 请求相关的 instrumentation
      try {
        const { FetchInstrumentation } = await import('@opentelemetry/instrumentation-fetch');
        const { XMLHttpRequestInstrumentation } = await import('@opentelemetry/instrumentation-xml-http-request');
        const { registerInstrumentations } = await import('@opentelemetry/instrumentation');

        // 配置允许传播 traceparent header 的URL规则
        const corsUrls = [
          /^https?:\/\/[^\/]*\/api/, // 匹配 /api 开头的路径
          /^https?:\/\/[^\/]*\/bapi/, // 匹配 /bapi 开头的路径
          /^https?:\/\/[^\/]*\/capi/, // 匹配 /capi 开头的路径
          /^https?:\/\/[^\/]*\/plazaApi/, // 匹配 /plazaApi 开头的路径
          /^https?:\/\/[^\/]*\/uapi/ // 匹配 /uapi 开头的路径
        ];

        // 🔥 创建URL过滤函数来解决类型错误
        const shouldIgnoreUrl = (url: string): boolean => {
          try {
            const urlObj = new URL(url, window.location.origin);
            const pathname = urlObj.pathname;

            // 忽略远程采样请求
            if (pathname.includes('/sampling')) {
              return true; // 忽略采样请求
            }

            const allowedPrefixes = ['/api/', '/bapi/', '/capi/', '/plazaApi/', '/uapi/'];
            const isAllowed = allowedPrefixes.some(prefix => pathname.startsWith(prefix));
            return !isAllowed; // 返回true表示忽略，false表示追踪
          } catch (error) {
            return true; // URL解析失败时忽略
          }
        };

        // 注册HTTP请求追踪instrumentations
        registerInstrumentations({
          instrumentations: [
            new FetchInstrumentation({
              ignoreUrls: [
                shouldIgnoreUrl as any, // 使用类型断言解决类型问题
                // 忽略规则
                /\/sampling/,  // 忽略所有包含/sampling的请求
                /\/g\//,
                /\/gsi\//,
                /\/chat\//,
                /\/_nuxt\//,
                /\/static\//,
                /\/assets\//,
                /\/favicon\./,
                /\/robots\.txt/,
                /\.json$/,
                /\.js$/,
                /\.css$/,
                /\.png$/,
                /\.jpg$/,
                /\.jpeg$/,
                /\.gif$/,
                /\.svg$/,
                /\.ico$/,
                /\.woff$/,
                /\.woff2$/,
                /\.ttf$/,
                /\.eot$/
              ],
              propagateTraceHeaderCorsUrls: corsUrls,
              clearTimingResources: true
            }),
            new XMLHttpRequestInstrumentation({
              ignoreUrls: [
                shouldIgnoreUrl as any, // 使用类型断言解决类型问题
                // 忽略规则
                /\/sampling/,  // 忽略所有包含/sampling的请求
                /\/g\//,
                /\/gsi\//,
                /\/chat\//,
                /\/_nuxt\//,
                /\/static\//,
                /\/assets\//,
                /\/favicon\./,
                /\/robots\.txt/,
                /\.json$/,
                /\.js$/,
                /\.css$/,
                /\.png$/,
                /\.jpg$/,
                /\.jpeg$/,
                /\.gif$/,
                /\.svg$/,
                /\.ico$/,
                /\.woff$/,
                /\.woff2$/,
                /\.ttf$/,
                /\.eot$/
              ],
              propagateTraceHeaderCorsUrls: corsUrls,
              clearTimingResources: true
            })
          ]
        });

      } catch (autoInstrError) {
        console.warn('HTTP instrumentation setup failed:', autoInstrError);
      }

    } catch (error) {
      console.error('❌ Failed to initialize OpenTelemetry:', error);
      throw error;
    }
  }

  getTracer(name: string, version?: string) {
    return trace.getTracer(name, version);
  }
}

export const telemetryManager = new TelemetryManager();

export function getTracer(name: string, version?: string) {
  return telemetryManager.getTracer(name, version);
}

// 🔥 添加自定义属性的辅助函数
export function addCustomAttributes(span: any, attributes: Record<string, any>) {
  try {
    span.setAttributes(attributes);
  } catch (error) {
    console.warn('Failed to add custom attributes:', error);
  }
}

// 🔥 获取性能相关的属性
export function getPerformanceAttributes(): Record<string, any> {
  const attrs: Record<string, any> = {};

  if (typeof performance !== 'undefined') {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigation) {
      attrs['perf.dom_load_time'] = navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart;
      attrs['perf.page_load_time'] = navigation.loadEventEnd - navigation.loadEventStart;
      attrs['perf.ttfb'] = navigation.responseStart - navigation.requestStart;
    }

    // 内存信息
    if ((performance as any).memory) {
      attrs['perf.memory_used'] = (performance as any).memory.usedJSHeapSize;
      attrs['perf.memory_total'] = (performance as any).memory.totalJSHeapSize;
    }
  }

  // 网络连接信息
  if (typeof navigator !== 'undefined' && (navigator as any).connection) {
    const connection = (navigator as any).connection;
    attrs['network.type'] = connection.effectiveType;
    attrs['network.downlink'] = connection.downlink;
    attrs['network.rtt'] = connection.rtt;
  }

  return attrs;
}

export const createTelemetryConfig = (
  environment: string,  // 改为 string 类型以支持 dev-production
  enableJaegerRemoteSampling = true
): TelemetryConfig => {
  const baseConfig = {
    serviceName: 'exchange-web',
    environment
  };

  // 基础Jaeger配置（所有环境通用）
  const createJaegerConfig = (baseUrl: string): JaegerRemoteSamplingConfig => ({
    enabled: enableJaegerRemoteSampling,
    endpoint: baseUrl,  // SDK 会自动拼接 /sampling?service={serviceName}
    initialSamplingRate: 1 // 初始100%采样率，请求后会被远程配置覆盖
  });

  // 处理 dev-production 作为 production
  const normalizedEnv = environment === 'dev-production' ? 'production' : environment;

  switch (normalizedEnv) {
  case 'development':
    return {
      ...baseConfig,
      enableConsole: false,
      otlpEndpoint: 'https://webtrace.test.infra.be2pzzl3bhmz.tbinner.com/v1/traces',
      samplingRate: 1.0,
      otlpHeaders: {
        'Content-Type': 'application/json'
      },
      jaegerRemoteSampling: createJaegerConfig(
        'https://otel-sampling-http.test1.wcsbapp.com'
      )
    };
  case 'test':
    return {
      ...baseConfig,
      enableConsole: false,
      otlpEndpoint: 'https://webtrace.test.infra.be2pzzl3bhmz.tbinner.com/v1/traces',
      samplingRate: 1.0,
      otlpHeaders: {
        'Content-Type': 'application/json'
      },
      jaegerRemoteSampling: createJaegerConfig(
        'https://otel-sampling-http.test1.wcsbapp.com'
      )
    };
  case 'production':
  case 'dev-production':  // 显式处理 dev-production
    return {
      ...baseConfig,
      enableConsole: false,
      otlpEndpoint: 'https://otel-http.toobit.com/v1/traces',
      samplingRate: 1,
      otlpHeaders: {
        'Content-Type': 'application/json'
      },
      jaegerRemoteSampling: createJaegerConfig(
        'https://otel-sampling-http.toobit.com'
      )
    };
  default:
    return {
      ...baseConfig,
      enableConsole: true,
      samplingRate: 1.0,
      jaegerRemoteSampling: enableJaegerRemoteSampling
        ? createJaegerConfig(
          'https://otel-sampling-http.toobit.com'
        )
        : undefined
    };
  }
};

// 导出工具函数：检查是否在客户端环境
export const isClientEnvironment = (): boolean => {
  const isBrowser = typeof window !== 'undefined' && typeof window.document !== 'undefined';
  const isSSR = typeof window !== 'undefined' && !!(window as any).__SSR__;
  const isNode = typeof process !== 'undefined' && process.versions && process.versions.node;
  return isBrowser && !isSSR && !isNode;
};
